/**
 * Shared time constants for `apps/web`.
 *
 * PATTERN: any cache, debounce, or timeout used in multiple places—or encoding
 * a product decision—lives here,
 * not as a component-local `const`.
 *
 * These values were duplicated
 * in ~12 files as `5 * 60 * 1000` and `300_000`.
 * Policy changes required every edit
 * and risked divergence. Import here.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;

/** Milliseconds in a minute. Base for deriving all other durations. */
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Milliseconds in an hour. Used by the foreground reminders window. */
export const ONE_HOUR_MS = MINUTES_PER_HOUR * MINUTE_MS;

/** Minutes a cached datum is considered fresh (offline cache and session). */
const STALE_MINUTES = 5;

/**
 * Default `staleTime` for offline-first queries (PowerSync + TanStack).
 *
 * Data syncs locally through PowerSync, so a generous `staleTime` (5 min)
 * avoids redundant refetches/re-renders with no real risk of showing stale
 * data. It is also the global `queryClient` default.
 */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` for session/authentication queries.
 *
 * Kept as its own constant (although it currently matches the offline cache
 * value) because it is an independent decision: it must stay aligned across
 * `useSession`, the `_app` route guard, and `session-bridge`.
 */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * Time ceiling for route `beforeLoad` session checks.
 *
 * P23/CC-60 · I1-bis — WHY SHARED:
 * `_app/route.tsx` had a local
 * `SESSION_CHECK_TIMEOUT`; `_auth/route.tsx` had none.
 * Browser tests with `get-session` STALLED (not 401/5xx) showed
 * `/login` EMPTY at 3/6/10/15/20 s, even in production,
 * while `/` loaded via the `_app` ceiling.
 * The defense existed
 * but missed the route that needed it.
 *
 * Expiry does NOT cancel the request;
 * it later syncs machine + cache.
 * Cost: at most a login flicker, never a blank screen.
 */
export const SESSION_CHECK_TIMEOUT_MS = 1500;

/**
 * Debounce for text inputs persisted while typing (detail title and notes,
 * subtasks). Balances typing smoothness against mutation count.
 */
export const DEBOUNCE_DELAY_MS = 400;

/** Seconds before revoking a download's object URL (see constant). */
const OBJECT_URL_REVOKE_SECONDS = 10;

/**
 * Grace period before revoking a `blob:` object URL after triggering a download.
 *
 * It must comfortably exceed the time the browser takes to consume the
 * `<a download>` href; revoking earlier would abort the download.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = OBJECT_URL_REVOKE_SECONDS * MS_PER_SECOND;
